import { useApolloClient } from '@apollo/client';
import type { CancelTokenSource } from 'axios';
import React, { useCallback } from 'react';
import type { DropzoneState } from 'react-dropzone';

import type { ParseTagSheetErrorResponse } from '../../../../../../apis/campaignGraphqlManager/dcmTagSheet';
import { parseTagSheet } from '../../../../../../apis/campaignGraphqlManager/dcmTagSheet';
import type { Get_Lineitems_By_Campaignid_ListQuery } from '../../../../../../apis/graphql/types';
import { getErrorMessageFromGraphQlErrors } from '../../../../../../apis/graphql/utils';
import { openToastAlert } from '../../../../../../common/ToastAlert/toastAlert';
import { GENERAL_ERROR_MESSAGE } from '../../../../../../constants';
import { GET_LINEITEMS_BY_CAMPAIGNID_LIST } from '../../../../TraffickingPage/hooks/queries';
import type { LineItemModel } from '../../../../TraffickingPage/modelConverters';
import { lineItemNodeToModel } from '../../../../TraffickingPage/modelConverters';
import type { UseFileUploadResponse } from '../../../common/hooks/useFileUpload';
import useFileUpload from '../../../common/hooks/useFileUpload';
import type { TagSheetParseResult } from '../../types';
import { sanitizeAds } from '../../utils/sanitizeAds';
import { enrichDcmTagSheetDataWithIds } from '../utils';
import {
  FETCH_LINE_ITEMS_FAILURE_MESSAGE,
  LINE_ITEMS_LIMIT_EXCEEDED_DESCRIPTION,
  LINE_ITEMS_LIMIT_EXCEEDED_MESSAGE,
  NO_LINE_ITEMS_FOUND_DESCRIPTION,
  NO_LINE_ITEMS_FOUND_MESSAGE,
  TAG_SHEET_ACCEPTED_FORMATS,
  UNMATCHED_LINE_ITEMS_MESSAGE,
  UNSUPPORTED_VENDOR_FIELD,
  UNSUPPORTED_VENDOR_MESSAGE,
} from './constants';
import { handleParsedResultAndMergeWithLineItems, tagSheetFileValidator } from './utils';

export type UseTagSheetParseResponse = {
  hovering: boolean;
  percentUploaded?: number;
  loading: boolean;
  cancelRequest: () => void;
  fileAddedName: string | null;
  onParseTagSheet: () => Promise<void>;
} & Pick<DropzoneState, 'getRootProps' | 'getInputProps'>;

export type UseTagSheetParseProps = {
  campaignId: string;
  onSuccess: (result: TagSheetParseResult) => void;
};

const LINE_ITEMS_LIMIT = 1000;

const useTagSheetParse = ({ campaignId, onSuccess }: UseTagSheetParseProps): UseTagSheetParseResponse => {
  const client = useApolloClient();

  const fetchLineItems = useCallback(async (): Promise<LineItemModel[] | null> => {
    try {
      const { data, errors } = await client.query<Get_Lineitems_By_Campaignid_ListQuery>({
        query: GET_LINEITEMS_BY_CAMPAIGNID_LIST,
        variables: {
          filterOptions: { campaignIdList: [campaignId] },
          limit: LINE_ITEMS_LIMIT,
        },
        errorPolicy: 'all',
      });

      if (errors?.length) {
        openToastAlert({
          alertType: 'error',
          message: FETCH_LINE_ITEMS_FAILURE_MESSAGE,
          description: getErrorMessageFromGraphQlErrors(errors),
        });

        return null;
      }

      if (data.lineItems.total === 0) {
        openToastAlert({
          alertType: 'error',
          message: NO_LINE_ITEMS_FOUND_MESSAGE,
          description: NO_LINE_ITEMS_FOUND_DESCRIPTION,
        });

        return null;
      }

      // TODO: approve that we support up to 1000 line items
      if (data.lineItems.total > LINE_ITEMS_LIMIT) {
        openToastAlert({
          alertType: 'error',
          message: LINE_ITEMS_LIMIT_EXCEEDED_MESSAGE,
          description: LINE_ITEMS_LIMIT_EXCEEDED_DESCRIPTION,
        });

        return null;
      }

      return data.lineItems.edges.map(({ node }) => lineItemNodeToModel(node));
    } catch {
      openToastAlert({
        alertType: 'error',
        message: FETCH_LINE_ITEMS_FAILURE_MESSAGE,
        description: GENERAL_ERROR_MESSAGE,
      });

      return null;
    }
  }, [campaignId, client]);

  const uploadFile = useCallback(
    async (
      file: File,
      setPercentUploaded: (percent: number) => void,
      cancelTokenSource: CancelTokenSource
    ): Promise<void> => {
      const rawResult = await parseTagSheet(file, campaignId, setPercentUploaded, cancelTokenSource);

      const enrichedResult = enrichDcmTagSheetDataWithIds(rawResult);

      const lineItemModels = await fetchLineItems();

      if (!lineItemModels) {
        return;
      }

      const { result, unmatchedLineItemIds } = handleParsedResultAndMergeWithLineItems(enrichedResult, lineItemModels);

      if (unmatchedLineItemIds.length > 0) {
        openToastAlert({
          alertType: 'error',
          message: UNMATCHED_LINE_ITEMS_MESSAGE,
          description: `Line items not found: ${unmatchedLineItemIds.join(', ')}`,
        });

        return;
      }

      onSuccess(sanitizeAds(result));
    },
    [campaignId, fetchLineItems, onSuccess]
  );

  const onError = useCallback((e: unknown): void => {
    const error = e as ParseTagSheetErrorResponse;
    const hasUnsupportedVendor = error.details?.some((detail) => detail.field === UNSUPPORTED_VENDOR_FIELD);
    const description = hasUnsupportedVendor ? (
      <>
        {UNSUPPORTED_VENDOR_MESSAGE}
        <br />
        {error.message}
      </>
    ) : (
      error.message
    );

    openToastAlert({
      alertType: 'error',
      message: error.name,
      description,
    });
  }, []);

  const { onUpload, ...fileUpload }: UseFileUploadResponse = useFileUpload({
    accept: TAG_SHEET_ACCEPTED_FORMATS,
    validator: tagSheetFileValidator,
    uploadFile,
    onError,
  });

  return {
    ...fileUpload,
    onParseTagSheet: onUpload,
  };
};

export default useTagSheetParse;
